import { LitElement, html, css } from 'lit';
import { property, customElement, query } from 'lit/decorators.js';

import {
  formatDate,
  formatTime,
  RetourOrderStatusReadyToCheck,
  RetourOrderStatusBeingChecked,
  formatDateInput,
} from '../../util/util';
import '../../elements/langhout-button/langhout-button';
import '../../elements/langhout-checkbox/langhout-checkbox';
import '../../elements/add-comment/add-comment';
import '../../elements/edit-product/edit-product';
import { AddComment } from '../../elements/add-comment/add-comment';

import { authenticatedAPICall } from '../../client/api-client';
import { Camera, CameraResultType } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { closeIcon } from '../icons/icons';

import {
  Comment,
  Document as RetourDocument,
  DocumentType,
  PickupOrder,
  PickupSubOrder,
  Product,
  RetourOrder,
  RetourOrderProduct,
} from '../../types/types';
import { showNotification } from '../../LanghoutBetonApp';
import { EditProduct } from '../../elements/edit-product/edit-product';

@customElement('retour-order')
export class RetourOrderElement extends LitElement {
  static styles = css`
    .card {
      border: 1px solid var(--text-color-main);
      padding: 12px;
      margin-bottom: 20px;
      border-radius: 10px;
      position: relative;
    }

    .row {
      width: 100%;
      display: inline-flex;
      flex-direction: row;
      justify-content: left;
    }

    .wrap-row {
      flex-wrap: wrap;
      flex-flow: row wrap;
    }

    .prop-wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      flex-flow: nowrap;
    }

    .prod-row {
      margin-top: 10px;
      text-align: left;
    }

    h3 {
      margin: 0;
    }

    .fill {
      flex-grow: 1;
    }

    .bottom-text {
      margin-top: 10px;
    }

    .label {
      color: var(--primary-color);
      min-width: 130px;
      text-align: left;
    }

    .normal-text {
      text-align: left;
      max-width: 120px;
      padding-right: 15px;
      overflow-wrap: break-word;
    }

    @media (min-width: 475px) {
      .normal-text {
        min-width: 120px;
      }
    }

    .controls {
      display: flex;
    }

    @media (max-width: 475px) {
      .controls {
        flex-direction: column;
      }

      #retourIcon {
        display: none;
      }
    }

    langhout-button {
      margin-top: 20px;
    }

    .button-container {
      max-width: 200px;
    }

    .order-icon {
      position: absolute;
      top: 10px;
      right: 10px;
    }

    svg {
      width: 30px;
      height: 30px;
    }

    .divider {
      margin-top: 10px;
      margin-bottom: 5px;
      border-top: 1px solid black;
    }

    .divider-small {
      margin-left: 10px;
      margin-right: 10px;
      margin-top: 10px;
      margin-bottom: 5px;
      border-top: 1px solid black;
    }

    .icon-space {
      margin-top: 10px;
      width: 70px;
      min-width: 70px;
      display: flex;
      flex-direction: row;
      justify-content: center;
    }

    .checked-space {
      width: 40px;
      min-width: 40px;
    }

    .prod-amount-text {
      color: var(--primary-color);
      font-weight: bold;
      margin-right: 20px;
      min-width: 63px;
    }

    .comment-wrapper {
      margin-top: 10px;
      padding: 10px;
      border: 1px solid var(--text-color-main);
      border-radius: 10px;
    }

    .comment-user-name,
    .comment-time {
      color: var(--primary-color);
      font-weight: bold;
    }

    .document-list {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-bottom: 10px;
    }

    .document-label {
      color: var(--primary-color);
      font-weight: bold;
      text-align: left;
      margin-bottom: 8px;
    }

    .document-icon-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 10px;
      border: 1px solid var(--text-color-main);
      background: white;
      color: var(--primary-color);
      cursor: pointer;
      padding: 0;
    }

    .document-icon-button.loading {
      opacity: 0.6;
      pointer-events: none;
    }

    .document-icon-button svg {
      width: 24px;
      height: 24px;
    }

    #documentModal {
      position: fixed;
      inset: 0;
      display: none;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.6);
      z-index: 10000;
      padding: 20px;
      box-sizing: border-box;
    }

    :host([documentModalVisible]) #documentModal {
      display: flex;
    }

    .document-modal-content {
      width: min(960px, 100%);
      max-height: 90vh;
      background: white;
      border-radius: 12px;
      padding: 16px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .close-icon {
      cursor: pointer;
      color: var(--primary-color);
    }

    .document-modal-title {
      font-weight: bold;
      text-align: left;
      overflow-wrap: break-word;
    }

    .document-preview-frame,
    .document-preview-image {
      width: 100%;
      min-height: 65vh;
      border: none;
      border-radius: 10px;
      background: #f6f6f6;
    }

    .document-preview-image {
      object-fit: contain;
      max-height: 65vh;
    }

    @media (min-width: 701px) {
      .prod-amount-text {
        display: inline-block;
        min-width: 180px;
      }
    }

    add-comment,
    edit-product {
      z-index: 9999;
      display: none;
      position: absolute;
      top: 0;
      left: 0;
    }

    :host([displayAddComment]) add-comment {
      display: flex;
    }

    :host([displayAddProduct]) edit-product {
      display: flex;
    }

    .warning {
      margin-top: 15px;
      color: red;
      font-weight: 900;
    }

    .book-directly-text {
      margin-left: 40px;
      text-align: left;
    }

    .button-row {
      margin-top: 10px;
      flex-wrap: wrap;
    }

    .half-width {
      width: 50%;
      min-width: 150px;
    }

    input {
      margin-top: 5px;
      padding: 5px;
      text-align: left;
      max-width: 125px;
      margin-right: 15px;
      overflow-wrap: break-word;
      border-radius: 5px;
      font-size: 16px;
    }

    .prod-desc {
      max-width: 250px;
    }

    :host([disableButtons]) {
      pointer-events: none;
      overflow: hidden;
      opacity: 0.7;
      background-color: red;
    }
  `;

  @property({ type: Object, attribute: true, reflect: true })
  public retourOrder: RetourOrder = <RetourOrder>{};

  @property({ type: Boolean, attribute: true, reflect: true })
  public displayOnly = false;

  @property({ type: Boolean, reflect: true })
  private displayAddComment = false;

  @property({ type: Boolean, reflect: true })
  private displayAddProduct = false;

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  private debouncedUpdate: Function;

  @query('#orderID')
  private orderID!: HTMLInputElement;

  @query('#customerName')
  private customerName!: HTMLInputElement;

  @query('#shippingID')
  private shippingID!: HTMLInputElement;

  @query('#transporter')
  private transporter!: HTMLInputElement;

  @query('#licensePlate')
  private licensePlate!: HTMLInputElement;

  @query('#retourDate')
  private retourDate!: HTMLInputElement;

  private isEditProduct = false;

  private editProduct: RetourOrderProduct | null = null;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Boolean, reflect: true })
  private documentModalVisible = false;

  @property({ type: String })
  private previewDocumentURL = '';

  @property({ type: String })
  private previewDocumentType = '';

  @property({ type: String })
  private previewDocumentName = '';

  @property({ type: String })
  private loadingDocumentID = '';

  constructor() {
    super();
    this.debouncedUpdate = this.debounce((e: CustomEvent) => {
      if (e.detail.value !== this.retourOrder.id) {
        return;
      }
      this.updateRetourOrder();
    }, 100);
    document.addEventListener(
      'update-retour-order',
      this.debouncedUpdate as EventListener,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      'update-retour-order',
      this.debouncedUpdate as EventListener,
    );
    this.releasePreviewDocument();
  }

  render() {
    if (!this.retourOrder.id) {
      return html``;
    }
    return html`
      <edit-product
        id="edit-product-${this.retourOrder.id}"
        .product=${this.editProduct}
        .isEdit=${this.isEditProduct}
        @close="${async (e: CustomEvent) => {
          this.displayAddProduct = false;
          if (!e.detail.amount || !e.detail.id) {
            return;
          }
          if (this.isEditProduct) {
            for (let i = 0; i < this.retourOrder.products.length; i++) {
              if (this.retourOrder.products[i].id === e.detail.old_id) {
                this.retourOrder.products[i].id = e.detail.id;
                this.retourOrder.products[i].amount = e.detail.amount;
              }
            }
          } else {
            this.retourOrder.products.push(<RetourOrderProduct>{
              id: e.detail.id,
              amount: e.detail.amount,
            });
          }
          await this.updateRetourOrder();
          const event = new CustomEvent('refresh-tasks', {
            detail: {},
          });
          this.dispatchEvent(event);
        }}"
      ></edit-product>
      <add-comment
        id="add-comment-${this.retourOrder.id}"
        url="/retour-orders/${this.retourOrder.id}/comment"
        @close="${() => {
          this.displayAddComment = false;
        }}"
      ></add-comment>
      ${this.renderDocumentModal()}
      <div class="card">
        <div class="row wrap-row">
          <div class="prop-wrapper">
            <div class="label">Order:</div>
            ${this.renderRetourOrderIDValue(this.retourOrder)}
          </div>
          <div class="prop-wrapper">
            <div class="label">Retourdatum:</div>
            ${this.renderRetourDateValue(this.retourOrder)}
          </div>
          <div class="fill"></div>
          <div class="icon-space">${this.renderRetourIcon()}</div>
        </div>
        <div class="row wrap-row">
          <div class="prop-wrapper">
            <div class="label">Klant:</div>
            ${this.renderCustomNameValue(this.retourOrder)}
          </div>
          <div class="prop-wrapper">
            <div class="label">Kenteken:</div>
            ${this.renderLicensePlateValue(this.retourOrder)}
          </div>
        </div>
        <div class="row wrap-row">
          <div class="prop-wrapper">
            <div class="label">Vracht ID:</div>
            ${this.renderShippingIDValue(this.retourOrder)}
          </div>
          <div class="prop-wrapper">
            <div class="label">Transporteur:</div>
            ${this.renderTransporterValue(this.retourOrder)}
          </div>
        </div>

        <div class="divider"></div>
        ${this.renderRetourOrderDocuments()}
        ${this.retourOrder.products.map(product => {
          if (this.displayOnly) {
            return html` <div class="row prod-row">
              ${this.renderPhotoAddedIcon(product)}
              <span class="prod-amount-text">
                ${product.amount_packs}
                ${this.getAmountText(
                  product.amount_packs,
                  product.product_type,
                )}
                ${this.getPiecesText(product.amount, product.product_type)}
              </span>
              <span> ${product.description} </span>
            </div>`;
          }
          return html`
            <div class="row prod-row">
              ${this.renderPhotoAddedIcon(product)}
              <span class="prod-amount-text">
                ${product.amount_packs}
                ${this.getAmountText(
                  product.amount_packs,
                  product.product_type,
                )}
                ${this.getPiecesText(product.amount, product.product_type)}
              </span>
              <span class="prod-desc"> ${product.description} </span>
              <div class="fill"></div>
              <div class="controls">${this.renderAllControlls(product)}</div>
            </div>
          `;
        })}
        ${this.renderAddProductIcon()} ${this.renderComments(this.retourOrder)}
        <div class="row button-row">
          <div class="half-width row button-row">
            <div class="button-container">${this.renderAddCommentButton()}</div>
            <div class="fill"></div>
          </div>
          <div class="half-width row button-row">
            ${this.renderButton(this.retourOrder)}
          </div>
        </div>
      </div>
    `;
  }

  renderRetourOrderDocuments = () => {
    if (
      !this.retourOrder.documents ||
      this.retourOrder.documents.length === 0
    ) {
      return html``;
    }

    return html`
      <div>
        <div class="document-label">Documenten</div>
        <div class="document-list">
          ${this.retourOrder.documents.map(document => {
            return this.renderRetourOrderDocument(document);
          })}
        </div>
        <div class="divider"></div>
      </div>
    `;
  };

  renderRetourOrderDocument = (document: RetourDocument) => {
    const isLoading = this.loadingDocumentID === document.id;

    return html`
      <button
        class="document-icon-button ${isLoading ? 'loading' : ''}"
        title=${`Document ${document.id}`}
        @click=${async () => {
          await this.openDocument(document);
        }}
      >
        ${this.renderDocumentIcon(document.type)}
      </button>
    `;
  };

  renderDocumentIcon = (mimeType: string) => {
    if (this.isPDFDocument(mimeType)) {
      return html`
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="feather feather-file-text"
        >
          <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
          ></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <line x1="10" y1="9" x2="8" y2="9"></line>
        </svg>
      `;
    }

    return html`
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="feather feather-image"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
        <circle cx="8.5" cy="8.5" r="1.5"></circle>
        <polyline points="21 15 16 10 5 21"></polyline>
      </svg>
    `;
  };

  renderDocumentModal = () => {
    if (!this.documentModalVisible || !this.previewDocumentURL) {
      return html``;
    }

    return html`
      <div
        id="documentModal"
        @click=${(event: MouseEvent) => {
          if (event.target === event.currentTarget) {
            this.closeDocumentModal();
          }
        }}
      >
        <div class="document-modal-content">
          <div class="row">
            <div class="document-modal-title">${this.previewDocumentName}</div>
            <div class="fill"></div>
            <div
              class="close-icon"
              @click=${() => {
                this.closeDocumentModal();
              }}
            >
              ${closeIcon()}
            </div>
          </div>
          ${this.renderDocumentPreview()}
        </div>
      </div>
    `;
  };

  renderDocumentPreview = () => {
    if (this.isPDFDocument(this.previewDocumentType)) {
      if (Capacitor.getPlatform() === 'android') {
        return html`
          <div
            class="document-preview-frame"
            style="display:flex;align-items:center;justify-content:center;flex-direction:column;gap:16px;"
          >
            <p>PDF weergave is niet beschikbaar op Android.</p>
            <langhout-button
              text="Download PDF"
              @click=${() => this.downloadPDF()}
            ></langhout-button>
          </div>
        `;
      }
      return html`
        <iframe
          class="document-preview-frame"
          src=${this.previewDocumentURL}
          title=${this.previewDocumentName || 'Retour order document'}
        ></iframe>
      `;
    }

    return html`
      <img
        class="document-preview-image"
        src=${this.previewDocumentURL}
        alt=${this.previewDocumentName || 'Retour order document'}
      />
    `;
  };

  private downloadPDF = async () => {
    const base64 = this.previewDocumentURL.split(',')[1];
    const fileName = `${this.previewDocumentName || 'document'}.pdf`;
    try {
      await Filesystem.writeFile({
        path: fileName,
        data: base64,
        directory: Directory.Documents,
      });
      showNotification(`PDF opgeslagen: ${fileName}`);
    } catch (error) {
      console.error('PDF download failed:', error);
      showNotification('PDF kon niet worden opgeslagen');
    }
  };

  openDocument = async (document: RetourDocument) => {
    if (!this.retourOrder.id || !document.id) {
      return;
    }

    this.loadingDocumentID = document.id;
    this.disableButtons = true;

    try {
      const path = `/retour-orders/${this.retourOrder.id}/documents/${document.id}`;
      const result = await authenticatedAPICall(path, 'GET', new Headers());
      if (!result || !result.body) {
        return;
      }

      const previewDocument = <RetourDocument>result.body;
      if (!previewDocument.document || !previewDocument.type) {
        showNotification('Document kon niet worden geladen');
        return;
      }

      this.releasePreviewDocument();
      this.previewDocumentURL = `data:${previewDocument.type};base64,${previewDocument.document}`;
      this.previewDocumentType = previewDocument.type || document.type;
      this.previewDocumentName = `Document ${previewDocument.id || document.id}`;
      this.documentModalVisible = true;
    } finally {
      this.loadingDocumentID = '';
      this.disableButtons = false;
    }
  };

  closeDocumentModal = () => {
    this.documentModalVisible = false;
    this.releasePreviewDocument();
  };

  releasePreviewDocument = () => {
    this.previewDocumentURL = '';
    this.previewDocumentType = '';
    this.previewDocumentName = '';
  };

  isPDFDocument = (mimeType: string) => {
    if (!mimeType) {
      return false;
    }
    return mimeType.toLowerCase() === DocumentType.PDF;
  };

  renderAddProductIcon = () => {
    if (this.retourOrder.status !== RetourOrderStatusBeingChecked) {
      return html``;
    }
    return html`
     <div class="row">
          <div class="fill"></div>
          <div class="icon-space" @click="${() => {
            this.isEditProduct = false;
            this.editProduct = null;
            this.displayAddProduct = true;
            this.focusAddProduct();
          }}">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#014E2A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-plus-square"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
      </div>
      </div>
      </div>
    `;
  };

  focusAddProduct = () => {
    setTimeout(() => {
      if (this.shadowRoot) {
        const editProductElement = <EditProduct>(
          this.shadowRoot.querySelector(`#edit-product-${this.retourOrder.id}`)
        );
        editProductElement.focus();
      }
    }, 10);
  };

  renderRetourOrderIDValue = (retourOrder: RetourOrder) => {
    if (retourOrder.status === RetourOrderStatusBeingChecked) {
      return html`<input
        type="number"
        id="orderID"
        name="orderID"
        min="0"
        pattern="[0-9]*"
        value=${this.retourOrderIDValue(retourOrder)}
        @input="${() => {
          this.inputChangedEvent();
        }}"
      />`;
    }
    return html`<div class="normal-text">
      ${this.retourOrderIDValue(retourOrder)}
    </div>`;
  };

  retourOrderIDValue = (retourOrder: RetourOrder): string => {
    const orderID = retourOrder.order_id;
    if (orderID == 0) {
      return '';
    }
    return orderID.toString();
  };

  renderCustomNameValue = (retourOrder: RetourOrder) => {
    if (retourOrder.status === RetourOrderStatusBeingChecked) {
      return html`<input
        type="text"
        id="customerName"
        name="customerName"
        value=${retourOrder.customer_name}
        @input="${() => {
          this.inputChangedEvent();
        }}"
      />`;
    }
    return html`<div class="normal-text">${retourOrder.customer_name}</div>`;
  };

  renderShippingIDValue = (retourOrder: RetourOrder) => {
    if (retourOrder.status === RetourOrderStatusBeingChecked) {
      return html`<input
        type="text"
        id="shippingID"
        name="shippingID"
        value=${retourOrder.shipping_id}
        @input="${() => {
          this.inputChangedEvent();
        }}"
      />`;
    }
    return html`<div class="normal-text">${retourOrder.shipping_id}</div>`;
  };

  renderTransporterValue = (retourOrder: RetourOrder) => {
    if (retourOrder.status === RetourOrderStatusBeingChecked) {
      return html`<input
        type="text"
        id="transporter"
        name="transporter"
        value=${retourOrder.transporter}
        @input="${() => {
          this.inputChangedEvent();
        }}"
      />`;
    }
    return html`<div class="normal-text">${retourOrder.transporter}</div>`;
  };

  renderLicensePlateValue = (retourOrder: RetourOrder) => {
    if (retourOrder.status === RetourOrderStatusBeingChecked) {
      return html`<input
        type="text"
        id="licensePlate"
        name="licensePlate"
        value=${retourOrder.license_plate}
        @input="${() => {
          this.inputChangedEvent();
        }}"
      />`;
    }
    return html`<div class="normal-text">${retourOrder.license_plate}</div>`;
  };

  renderRetourDateValue = (retourOrder: RetourOrder) => {
    if (retourOrder.status === RetourOrderStatusBeingChecked) {
      return html`<input
        type="date"
        id="retourDate"
        name="retourDate"
        value=${formatDateInput(this.retourOrder.retour_date)}
        @input="${() => {
          this.inputChangedEvent();
        }}"
      />`;
    }

    return html`<div class="normal-text">
      ${formatDate(this.retourOrder.retour_date)}
    </div>`;
  };

  inputChangedEvent = () => {
    const refreshTasksEvent = new CustomEvent('update-retour-order', {
      detail: {
        value: this.retourOrder.id,
      },
    });
    document.dispatchEvent(refreshTasksEvent);
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  debounce = (func: Function, delay: number) => {
    let timeoutId: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };
  renderAllControlls = (product: RetourOrderProduct) => {
    if (this.retourOrder.status !== RetourOrderStatusBeingChecked) {
      return html``;
    }
    return html`
      ${this.renderControlIcons(product)} ${this.renderPhotoIcon(product)}
    `;
  };

  renderPhotoAddedIcon = (product: RetourOrderProduct) => {
    if (product.documents && product.documents.length > 0) {
      return html`
        <div class="checked-space">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#014E2A"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-check-square"
          >
            <polyline points="9 11 12 14 22 4"></polyline>
            <path
              d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
            ></path>
          </svg>
        </div>
      `;
    }
    return html` <div class="checked-space"></div> `;
  };

  renderControlIcons = (product: RetourOrderProduct) => {
    if (this.displayOnly) {
      return html``;
    }
    return html`
      <div
        class="icon-space"
        @click="${() => {
          this.editProduct = product;
          this.isEditProduct = true;
          this.displayAddProduct = true;
          this.focusAddProduct();
        }}"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#014E2A"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="feather feather-edit"
        >
          <path
            d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
          ></path>
          <path
            d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
          ></path>
        </svg>
      </div>
      <div class="icon-space">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="red"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="feather feather-trash-2"
          @click="${async () => {
            this.retourOrder.products = this.retourOrder.products.filter(
              p => p.id !== product.id,
            );
            await this.updateRetourOrder();
            const refreshTasksEvent = new CustomEvent('refresh-tasks', {
              detail: {},
            });
            this.dispatchEvent(refreshTasksEvent);
          }}"
        >
          <polyline points="3 6 5 6 21 6"></polyline>
          <path
            d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
          ></path>
          <line x1="10" y1="11" x2="10" y2="17"></line>
          <line x1="14" y1="11" x2="14" y2="17"></line>
        </svg>
      </div>
    `;
  };

  takePicture = async (product: RetourOrderProduct) => {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.Base64,
    });

    if (!image.base64String) {
      showNotification('Geen foto gemaakt');
      return;
    }
    this.disableButtons = true;
    await this.addPhotoAPICall(image.base64String, product);
    this.disableButtons = false;
    const refreshTasksEvent = new CustomEvent('refresh-retour-tasks', {
      detail: {
        value: 'refresh-retour-tasks',
      },
      bubbles: true,
    });
    document.dispatchEvent(refreshTasksEvent);
  };

  renderButton = (retourOrder: RetourOrder) => {
    if (this.displayOnly) {
      return html``;
    }
    let buttonText = 'Oppakken';
    switch (retourOrder.status) {
      case RetourOrderStatusBeingChecked:
        buttonText = 'Goedgekeurd';
        break;
    }

    return html`
      <div class="fill"></div>
      <div class="button-container">
        <langhout-button
          text="${buttonText}"
          @click="${() => {
            const event = new CustomEvent('button-clicked', {
              detail: {},
            });
            this.dispatchEvent(event);
          }}"
        ></langhout-button>
      </div>
    `;
  };

  renderComments = (retourOrder: RetourOrder) => {
    if (retourOrder.comments.length === 0) {
      return html``;
    }
    return html`
      ${retourOrder.comments.map((comment: Comment) => {
        return html`
          <div class="comment-wrapper">
            <div class="row">
              <div class="comment-user-name">${comment.user_name}</div>
              <div class="fill"></div>
              <div class="comment-time">
                ${formatTime(comment.timestamp, false)}
              </div>
            </div>
            <div class="row">${comment.message}</div>
          </div>
        `;
      })}
    `;
  };

  renderPhotoIcon = (product: RetourOrderProduct) => {
    if (product.documents && product.documents.length > 0) {
      return this.photoIcon('#014E2A', product);
    }
    return this.photoIcon('#de9221', product);
  };

  photoIcon = (color: string, product: RetourOrderProduct) => {
    return html`
      <div
        class="icon-space"
        @click="${() => {
          this.takePicture(product);
        }}"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="${color}"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="feather feather-camera"
        >
          <path
            d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
          ></path>
          <circle cx="12" cy="13" r="4"></circle>
        </svg>
      </div>
    `;
  };

  renderRetourIcon = () => {
    if (this.retourOrder.status === RetourOrderStatusReadyToCheck) {
      return this.retourIcon('#de9221');
    }
    return this.retourIcon('#014E2A');
  };

  retourIcon(color: string) {
    return html`
      <svg
        id="retourIcon"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="${color}"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="feather feather-corner-down-left"
      >
        <polyline points="9 10 4 15 9 20"></polyline>
        <path d="M20 4v7a4 4 0 0 1-4 4H4"></path>
      </svg>
    `;
  }

  renderAddCommentButton = () => {
    if (this.displayOnly) {
      return html``;
    }
    return html`
      <langhout-button
        reverse
        text="Opmerking toevoegen"
        @click="${() => {
          this.displayAddComment = true;
          setTimeout(() => {
            if (this.shadowRoot) {
              const addCommentElement = <AddComment>(
                this.shadowRoot.querySelector(
                  `#add-comment-${this.retourOrder.id}`,
                )
              );
              addCommentElement.focus();
            }
          }, 10);
        }}"
      ></langhout-button>
    `;
  };

  getWeightTotalText = (retourOrder: PickupOrder): string => {
    let totalWeight = 0;
    retourOrder.sub_orders.forEach((p: PickupSubOrder) => {
      p.products.forEach((p: Product) => {
        totalWeight += p.amount * p.weight;
      });
    });
    totalWeight = Math.round(totalWeight);

    return `${totalWeight.toLocaleString('nl-NL')} kg`;
  };

  getPiecesText = (amount: number, type: number): string => {
    if (type === 2) {
      return '';
    }
    if (amount === 1) {
      return `(${amount} st.)`;
    }
    return `(${amount} sts.)`;
  };

  getAmountText = (amount: number, type: number): string => {
    if (type === 2) {
      if (amount === 1) {
        return 'st.';
      }
      return 'sts.';
    }
    if (amount === 1) {
      return 'pak';
    }
    return 'pakken';
  };

  updateRetourOrder = async () => {
    let orderID = 0;
    if (this.orderID) {
      orderID = parseInt(this.orderID.value);
    }
    let customerName = '';
    if (this.customerName) {
      customerName = this.customerName.value;
    }
    let shippingID = '';
    if (this.shippingID) {
      shippingID = this.shippingID.value;
    }
    let transporter = '';
    if (this.transporter) {
      transporter = this.transporter.value;
    }
    let licensePlate = '';
    if (this.licensePlate) {
      licensePlate = this.licensePlate.value;
    }
    let retourDate = '';
    if (this.retourDate) {
      retourDate = new Date(this.retourDate.value).toISOString();
    }
    type RetourOrderProductPayload = {
      id: string;
      amount: number;
    };
    const products: RetourOrderProductPayload[] = [];
    this.retourOrder.products.forEach(product => {
      products.push({
        id: product.id,
        amount: product.amount,
      });
    });

    const body = {
      order_id: orderID,
      customer_name: customerName,
      shipping_id: shippingID,
      transporter: transporter,
      license_plate: licensePlate,
      retour_date: retourDate,
      products: products,
    };
    const path = `/retour-orders/${this.retourOrder.id}`;

    this.disableButtons = true;
    await authenticatedAPICall(
      path,
      'PUT',
      new Headers(),
      JSON.stringify(body),
    );
    this.disableButtons = false;
  };

  addPhotoAPICall = async (b64Img: string, product: RetourOrderProduct) => {
    const path = `/retour-orders/${this.retourOrder.id}/product-document-upload/${product.id}`;
    const body = {
      document: b64Img,
      type: DocumentType.Image,
    };
    const result = await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify(body),
    );

    if (
      result.statusCode !== undefined &&
      result.statusCode >= 200 &&
      result.statusCode < 300
    ) {
      showNotification('Foto toegevoegd');
    } else {
      console.error('Failed to add photo', result);
      showNotification('Fout bij toevoegen foto');
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'retour-order': RetourOrderElement;
  }
}
